import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Partner } from '../..'
import { Check, TextArea } from 'ui/fields'
import { useIntl } from 'react-intl'
import AutoComplete from 'ui/fields/Autocomplete'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import useDebounce from '../../../../hooks/useDebounce'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getMembersForOperationFilterList } from '../../../../api/summary'
import { Button, ButtonProps } from 'ui'

export type TransferForm = {
   originMember: number
   destinationMember: string
   reason?: string
   confirmation: boolean
}

type Props = {
   originMember: Partner
   onSubmit: (data: TransferForm) => void
   visible?: boolean
}

const TransferOperationFormContext = createContext<Props>({} as Props)

const TransferOperation = ({ children, visible = true, ...rest }: PropsWithChildren<Props>) => {
   const { reset, ...methodsRest } = useForm<TransferForm>({
      mode: 'onChange',
      defaultValues: {
         originMember: rest.originMember.externalId,
         confirmation: false,
      },
   })

   useEffect(() => {
      if (!visible) {
         reset()
      }
   }, [visible, reset])

   return (
      <TransferOperationFormContext.Provider value={{ ...rest }}>
         <FormProvider reset={reset} {...methodsRest}>
            {children}
         </FormProvider>
      </TransferOperationFormContext.Provider>
   )
}

const Form = () => {
   const intl = useIntl()
   const { originMember, onSubmit } = useContext(TransferOperationFormContext)
   const {
      handleSubmit,
      register,
      control,
      formState: { errors, dirtyFields },
   } = useFormContext<TransferForm>()
   const [searchValue, setSearchValue] = useState<string>('')
   const debouncedSearchValue = useDebounce(searchValue, 500)
   const { data, isFetching } = useQuery({
      queryKey: ['GET_MEMBERS_FOR_OPERATION_FILTER', originMember.externalId, debouncedSearchValue],
      queryFn: () =>
         getMembersForOperationFilterList({
            query: debouncedSearchValue,
            externalId: originMember.externalId,
            loyaltyPlanIds: originMember?.loyaltyPlans?.map((plan) => plan.loyaltyPlanId) ?? [],
         }),
      placeholderData: keepPreviousData,
      staleTime: 1000,
      retry: 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
   })

   return (
      <form onSubmit={handleSubmit(onSubmit)}>
         <div className="mb-6">
            <label className="inline-block mb-1.5">
               {intl.formatMessage({ id: 'partner.operation.transfer.originMember' })}
            </label>
            <div>
               <span className="text-neutral-400">{originMember.externalId}</span>&nbsp;&bull;&nbsp;
               {`${originMember.firstName} ${originMember.lastName}`}
            </div>
         </div>
         <Controller
            name="destinationMember"
            control={control}
            rules={{
               required: intl.formatMessage({ id: `partner.operation.errors.destinationMember` }),
            }}
            render={({ field: { value, onChange } }) => (
               <AutoComplete
                  label={intl.formatMessage({ id: 'partner.operation.transfer.destinationMember' })}
                  selectedValue={value}
                  onSelectedValueChange={onChange}
                  searchValue={searchValue}
                  onSearchValueChange={setSearchValue}
                  items={
                     data?.map((x) => ({
                        label: x.fullName,
                        value: x.externalId.toString(),
                        display: (
                           <div>
                              <span className="text-neutral-400">{x.externalId}</span>&nbsp;&bull;&nbsp;{x.fullName}
                           </div>
                        ),
                     })) ?? []
                  }
                  isLoading={isFetching}
                  showSelectedValue
                  error={errors?.destinationMember?.message}
                  showErrorText
                  isTouched={dirtyFields.destinationMember}
                  placeholder="Buscar"
               />
            )}
         />
         <TextArea
            label="Comentario (opcional)"
            {...register('reason')}
            error={errors?.reason?.message}
            isTouched={dirtyFields.reason}
            containerClassName="mb-6"
         />
         <Check
            containerClassName="mb-6"
            {...register('confirmation', {
               required: intl.formatMessage({ id: `partner.operation.errors.confirmation` }),
            })}
            label={intl.formatMessage({ id: 'partner.operation.transfer.confirmation' })}
            error={errors?.confirmation?.message}
            isTouched={dirtyFields.confirmation}
         />
      </form>
   )
}

const SubmitButton = ({ children, ...rest }: PropsWithChildren<ButtonProps>) => {
   const { handleSubmit } = useFormContext<TransferForm>()
   const { onSubmit } = useContext(TransferOperationFormContext)
   return (
      <Button {...rest} type="submit" onClick={handleSubmit(onSubmit)}>
         {children}
      </Button>
   )
}

TransferOperation.Form = Form
TransferOperation.SubmitButton = SubmitButton

export default TransferOperation
